import { useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../components/auth/AuthContext';
import { fcmTokenManager } from '../utils/fcm';

const replaceNicknameTokens = (text, nickname) => {
  if (!text || !nickname) return text || '';
  return text.replace(/%%%/g, nickname).replace(/％％％/g, nickname);
};

export const useFCM = () => {
  const { user, isAuthenticated } = useAuth();
  const swRegistrationRef = useRef(null);
  // Keep latest nickname in a ref so the foreground handler always reads fresh value
  // without needing to re-register the onMessage listener every time nickname changes
  const nicknameRef = useRef('');

  useEffect(() => {
    nicknameRef.current = user?.nickname || user?.username || '';
  }, [user?.nickname, user?.username]);

  const postToSW = useCallback((message) => {
    const sw = swRegistrationRef.current?.active;
    if (sw) sw.postMessage(message);
  }, []);

  const registerServiceWorker = useCallback(async () => {
    if (!('serviceWorker' in navigator)) return null;
    try {
      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      swRegistrationRef.current = registration;
      return registration;
    } catch (error) {
      console.error('Service worker registration failed:', error);
      return null;
    }
  }, []);

  const handleForegroundMessage = useCallback((payload) => {
    if (Notification.permission !== 'granted') return;

    const nickname = nicknameRef.current;
    const data = payload.data || {};
    const notification = payload.notification || {};

    const title = data.title || notification.title || '新通知';
    const rawBody = data.body || notification.body || '';
    const body = replaceNicknameTokens(rawBody, nickname);
    const memberId = data.member_id;

    const n = new Notification(title, {
      body,
      icon: '/logo.svg',
      tag: data.message_id || 'fcm-fg',
    });

    if (memberId) {
      n.onclick = () => {
        window.focus();
        window.location.href = `/members/${memberId}?src=push`;
      };
    }
  }, []); // stable — reads nickname via ref at call time

  // Initialize FCM when user logs in (runs once per login)
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    let cancelled = false;

    const init = async () => {
      await registerServiceWorker();
      if (cancelled) return;

      postToSW({ type: 'SET_NICKNAME', nickname: user.nickname || user.username || '' });

      await fcmTokenManager.initializePush(handleForegroundMessage);
    };

    init();

    return () => { cancelled = true; };
  }, [isAuthenticated, user?.id, registerServiceWorker, postToSW, handleForegroundMessage]);

  // Keep SW nickname in sync when nickname/username changes
  useEffect(() => {
    const nickname = user?.nickname || user?.username || '';
    if (nickname) {
      postToSW({ type: 'SET_NICKNAME', nickname });
    }
  }, [user?.nickname, user?.username, postToSW]);

  return null;
};
